import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import FlagIcon from "@mui/icons-material/Flag";
import dayjs from "dayjs";
import CommentIcon from "@mui/icons-material/Comment";
import { SeeMore } from "components/Text/SeeMore";
import { CompactStepper } from "components/Stepper/CompactStepper";

const TaskPreviewModal = ({
  task: {
    title,
    docDetails,
    detail,
    description,
    taskByName,
    taskToName,
    taskToOn,
    acceptanceStatus,
    acceptanceComment,
    acceptanceOn,
    deadline,
    doneStatus,
    doneOn,
    acknowledgeStatus,
    acknowledgeOn,
    flagged = false,
    alert,
    doneComment,
    acknowledgeComment,
  },
  children,
}) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const details = [
    {
      icon: <PersonOutlineIcon />,
      label: "Assigned By:",
      value: taskByName,
    },
    {
      icon: <PersonOutlineIcon />,
      label: "Assigned To:",
      value: taskToName,
    },
    {
      icon: <CalendarTodayIcon />,
      label: "Assigned:",
      value: dayjs(taskToOn).format("MMMM DD, YYYY"),
    },
    {
      icon: <CheckCircleOutlineIcon />,
      label: "Accepted:",
      value: acceptanceOn ? dayjs(acceptanceOn).format("MMMM DD, YYYY") : "N/A",
    },
    {
      icon: <HighlightOffIcon />,
      label: "Deadline:",
      value: dayjs(deadline).format("MMMM DD, YYYY"),
    },
    {
      icon: <CheckCircleOutlineIcon />,
      label: "Completed:",
      value: doneOn ? dayjs(doneOn).format("MMMM DD, YYYY") : "N/A",
    },
    {
      icon: <NotificationsNoneIcon />,
      label: "Alert:",
      value: alert + " day(s) before",
    },
  ];
  const remarksDetails = [
    {
      icon: <CommentIcon />,
      label: "Acceptance:",
      value: acceptanceComment ? <SeeMore text={acceptanceComment} /> : "N/A",
    },
    {
      icon: <CommentIcon />,
      label: "Done:",
      value: doneComment ? <SeeMore text={doneComment} /> : "N/A",
    },
    {
      icon: <CommentIcon />,
      label: "Acknowledge:",
      value: acknowledgeComment ? <SeeMore text={acknowledgeComment} /> : "N/A",
    },
  ];
  const stepperData = [
    { label: "Pending", isCurrentStepActive: true },
    {
      label: acceptanceStatus,
      isCurrentStepActive: acceptanceStatus === "Accepted",
    },
    { label: "Completed", isCurrentStepActive: !!doneStatus },
    { label: "Acknowledged", isCurrentStepActive: !!acknowledgeStatus },
  ];
  return (
    <>
      <span onClick={handleOpen} style={{ cursor: "pointer" }}>
        {children}
      </span>

      <Dialog
        fullScreen={fullScreen}
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: { borderRadius: 2, width: { sm: 600 } } }}
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              {title} - {detail}
            </Typography>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
            <Chip
              label={acceptanceStatus}
              color={acceptanceStatus === "Accepted" ? "primary" : "default"}
              size="small"
            />
            {flagged && (
              <Chip
                icon={<FlagIcon />}
                label="Flagged"
                color="error"
                size="small"
              />
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            <CompactStepper steps={stepperData} />
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Box mb={3}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              DESCRIPTION
            </Typography>
            <Typography variant="body1">
              <SeeMore text={description} />
            </Typography>
          </Box>

          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            DETAILS
          </Typography>
          <Grid container spacing={2}>
            {details.map((detail, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  {detail.icon}
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {detail.label}
                    </Typography>
                    <Typography variant="body1">{detail.value}</Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            REMARKS
          </Typography>
          <Grid container spacing={2}>
            {remarksDetails.map((detail, index) => (
              <Grid item xs={12} sm={12} key={index}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  {detail.icon}
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {detail.label}
                    </Typography>
                    <Typography variant="body1">{detail.value}</Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TaskPreviewModal;
